import type { SupportedLanguage } from 'src/i18n/types';

/**
 * Oversettelser av navn fra dabih-api-malen (issue #13272), nøklet på det norske
 * (nb) navnet. Brukes av `translateTemplateName` i template-content.ts.
 *
 * nb utelates (== nøkkelen). en/sv/da/pl er KI-genererte (POC) og bør
 * morsmål-gjennomgås før produksjon.
 *
 * Status: hovedmeny (pageGroups) + sidetitler er fylt inn. Navn på øvrige
 * mal-entiteter (dokumenter, retningslinjer, funksjoner, kontrakter, rapporter,
 * risikovurderinger m.fl.) fylles inn separat (se #13272-oppfølging).
 */
type TemplateTranslation = Partial<Record<Exclude<SupportedLanguage, 'nb'>, string>>;

export const TEMPLATE_TRANSLATIONS: Record<string, TemplateTranslation> = {
  // --- Hovedmeny: gruppe-navn (pageGroups.code) ---
  Aktuelt: { en: 'Current', sv: 'Aktuellt', da: 'Aktuelt', pl: 'Aktualne' },
  Registre: { en: 'Registers', sv: 'Register', da: 'Registre', pl: 'Rejestry' },
  Temasider: { en: 'Topic pages', sv: 'Temasidor', da: 'Temasider', pl: 'Strony tematyczne' },
  Organisasjon: { en: 'Organisation', sv: 'Organisation', da: 'Organisation', pl: 'Organizacja' },
  Diverse: { en: 'Miscellaneous', sv: 'Diverse', da: 'Diverse', pl: 'Różne' },

  // --- Sidetitler (pageGroups.pages.name) ---
  Overblikk: { en: 'Overview', sv: 'Översikt', da: 'Overblik', pl: 'Przegląd' },
  Ukeplan: { en: 'Weekly plan', sv: 'Veckoplan', da: 'Ugeplan', pl: 'Plan tygodnia' },
  Bemanningskalender: {
    en: 'Staffing calendar',
    sv: 'Bemanningskalender',
    da: 'Bemandingskalender',
    pl: 'Kalendarz obsady',
  },
  Møter: { en: 'Meetings', sv: 'Möten', da: 'Møder', pl: 'Spotkania' },
  Utstyrsoversikt: {
    en: 'Equipment overview',
    sv: 'Utrustningsöversikt',
    da: 'Udstyrsoversigt',
    pl: 'Przegląd sprzętu',
  },
  Stoffkartotek: { en: 'Chemical inventory', sv: 'Kemikalieförteckning', da: 'Stofkartotek', pl: 'Wykaz substancji' },
  Datadokumentasjon: {
    en: 'Data documentation',
    sv: 'Datadokumentation',
    da: 'Datadokumentation',
    pl: 'Dokumentacja danych',
  },
  Avvik: { en: 'Deviations', sv: 'Avvikelser', da: 'Afvigelser', pl: 'Odchylenia' },
  Risikovurderinger: { en: 'Risk assessments', sv: 'Riskbedömningar', da: 'Risikovurderinger', pl: 'Oceny ryzyka' },
  Samlinger: { en: 'Collections', sv: 'Samlingar', da: 'Samlinger', pl: 'Zbiory' },
  Ledelse: { en: 'Management', sv: 'Ledning', da: 'Ledelse', pl: 'Zarządzanie' },
  HMS: { en: 'HSE', sv: 'Arbetsmiljö', da: 'Arbejdsmiljø', pl: 'BHP' },
  'Klinisk virksomhet': {
    en: 'Clinical operations',
    sv: 'Klinisk verksamhet',
    da: 'Klinisk virksomhed',
    pl: 'Działalność kliniczna',
  },
  Utstyr: { en: 'Equipment', sv: 'Utrustning', da: 'Udstyr', pl: 'Sprzęt' },
  Datasikkerhet: { en: 'Data security', sv: 'Datasäkerhet', da: 'Datasikkerhed', pl: 'Bezpieczeństwo danych' },
  Kvalitetsarbeid: { en: 'Quality work', sv: 'Kvalitetsarbete', da: 'Kvalitetsarbejde', pl: 'Praca nad jakością' },
  Virksomhet: { en: 'Business', sv: 'Verksamhet', da: 'Virksomhed', pl: 'Działalność' },
  Personale: { en: 'Staff', sv: 'Personal', da: 'Personale', pl: 'Personel' },
  Samarbeidspartnere: {
    en: 'Partners',
    sv: 'Samarbetspartner',
    da: 'Samarbejdspartnere',
    pl: 'Partnerzy',
  },
  Personer: { en: 'People', sv: 'Personer', da: 'Personer', pl: 'Osoby' },
  'Kurs og veiledere': {
    en: 'Courses and guides',
    sv: 'Kurser och guider',
    da: 'Kurser og vejledninger',
    pl: 'Kursy i przewodniki',
  },
  Referanser: { en: 'References', sv: 'Referenser', da: 'Referencer', pl: 'Odniesienia' },
  Papirkurv: { en: 'Trash', sv: 'Papperskorg', da: 'Papirkurv', pl: 'Kosz' },
  // --- Mal-entiteter (dokumenter, retningslinjer, kontrakter, rapporter, risikovurderinger,
  //     funksjoner, møter, referanser) — KI-oversatt (POC), bør morsmål-gjennomgås ---
  'AMU-møte': {
    en: 'Work environment committee meeting',
    sv: 'Skyddskommittémöte',
    da: 'Arbejdsmiljøudvalgsmøde',
    pl: 'Posiedzenie komitetu BHP',
  },
  'Aksjonæravtale for fysioterapeuter med selskapstilskudd': {
    en: "Shareholders' agreement for physiotherapists with company operating subsidy",
    sv: 'Aktieägaravtal för fysioterapeuter med bolagsbidrag',
    da: 'Aktionæroverenskomst for fysioterapeuter med selskabstilskud',
    pl: 'Umowa wspólników dla fizjoterapeutów z dotacją operacyjną spółki',
  },
  'Akuttrom/skifterom': {
    en: 'Emergency room/dressing-change room',
    sv: 'Akutrum/omläggningsrum',
    da: 'Akutrum/skifterum',
    pl: 'Sala zabiegowa/sala opatrunkowa',
  },
  Alle: { en: 'All', sv: 'Alla', da: 'Alle', pl: 'Wszyscy' },
  'Ansettelseskontrakt for assistent uten autorisasjon i privat sektor': {
    en: 'Employment contract for assistant without authorisation in the private sector',
    sv: 'Anställningsavtal för assistent utan legitimation i privat sektor',
    da: 'Ansættelseskontrakt for assistent uden autorisation i privat sektor',
    pl: 'Umowa o pracę dla asystenta bez uprawnień zawodowych w sektorze prywatnym',
  },
  'Ansettelseskontrakt for tannhelsesekretær i privat sektor': {
    en: 'Employment contract for dental secretary in the private sector',
    sv: 'Anställningsavtal för tandsköterska i privat sektor',
    da: 'Ansættelseskontrakt for tandklinikassistent i privat sektor',
    pl: 'Umowa o pracę dla asystentki stomatologicznej w sektorze prywatnym',
  },
  'Ansettelseskontrakt for tannlege i privat sektor': {
    en: 'Employment contract for dentist in the private sector',
    sv: 'Anställningsavtal för tandläkare i privat sektor',
    da: 'Ansættelseskontrakt for tandlæge i privat sektor',
    pl: 'Umowa o pracę dla dentysty w sektorze prywatnym',
  },
  'Ansettelseskontrakt for tannpleier i privat sektor': {
    en: 'Employment contract for dental hygienist in the private sector',
    sv: 'Anställningsavtal för tandhygienist i privat sektor',
    da: 'Ansættelseskontrakt for tandplejer i privat sektor',
    pl: 'Umowa o pracę dla higienistki stomatologicznej w sektorze prywatnym',
  },
  Ansvarsforsikring: {
    en: 'Liability insurance',
    sv: 'Ansvarsförsäkring',
    da: 'Ansvarsforsikring',
    pl: 'Ubezpieczenie odpowiedzialności cywilnej',
  },
  Arbeidsavtale: { en: 'Employment contract', sv: 'Anställningsavtal', da: 'Ansættelseskontrakt', pl: 'Umowa o pracę' },
  'Arbeidsavtale for fysioterapeut i selskap med driftstilskudd': {
    en: 'Employment contract for physiotherapist in a company with operating subsidy',
    sv: 'Anställningsavtal för fysioterapeut i bolag med driftsbidrag',
    da: 'Ansættelseskontrakt for fysioterapeut i selskab med driftstilskud',
    pl: 'Umowa o pracę dla fizjoterapeuty w spółce z dotacją operacyjną',
  },
  'Arbeidsplikt og beordring for helsepersonell': {
    en: 'Duty to work and emergency assignment for healthcare personnel',
    sv: 'Arbetsplikt och beordring för hälso- och sjukvårdspersonal',
    da: 'Arbejdspligt og beordring for sundhedspersonale',
    pl: 'Obowiązek pracy i oddelegowanie służbowe personelu medycznego',
  },
  'Avtale med Norsk helsenett': {
    en: 'Agreement with the Norwegian Health Network (Norsk helsenett)',
    sv: 'Avtal med Norsk helsenett',
    da: 'Aftale med Norsk helsenett',
    pl: 'Umowa z Norweską Siecią Zdrowia (Norsk helsenett)',
  },
  'Avtale med laboratorium om elektronisk kommunikasjon': {
    en: 'Agreement with laboratory on electronic communication',
    sv: 'Avtal med laboratorium om elektronisk kommunikation',
    da: 'Aftale med laboratorium om elektronisk kommunikation',
    pl: 'Umowa z laboratorium dotycząca komunikacji elektronicznej',
  },
  'Avtale om annen ordning enn verneombud': {
    en: 'Agreement on an arrangement other than a safety representative',
    sv: 'Avtal om annan ordning än skyddsombud',
    da: 'Aftale om anden ordning end arbejdsmiljørepræsentant',
    pl: 'Umowa o rozwiązaniu innym niż przedstawiciel ds. BHP',
  },
  'Avtale om bruk av informasjonssystemet': {
    en: 'Agreement on use of the information system',
    sv: 'Avtal om användning av informationssystemet',
    da: 'Aftale om brug af informationssystemet',
    pl: 'Umowa o korzystaniu z systemu informacyjnego',
  },
  'Avtale om felles pasientjournal': {
    en: 'Agreement on a shared patient record',
    sv: 'Avtal om gemensam patientjournal',
    da: 'Aftale om fælles patientjournal',
    pl: 'Umowa o wspólnej dokumentacji medycznej pacjenta',
  },
  'Avtale om forsvarlig opplæring av verneombud': {
    en: 'Agreement on adequate training of safety representatives',
    sv: 'Avtal om försvarlig utbildning av skyddsombud',
    da: 'Aftale om forsvarlig oplæring af arbejdsmiljørepræsentant',
    pl: 'Umowa o należytym przeszkoleniu przedstawiciela ds. BHP',
  },
  'Avtale om fysisk adgang til virksomheten': {
    en: 'Agreement on physical access to the premises',
    sv: 'Avtal om fysiskt tillträde till verksamheten',
    da: 'Aftale om fysisk adgang til virksomheden',
    pl: 'Umowa o fizycznym dostępie do obiektu',
  },
  'Avtale om kjøp av tannhelsetjenester (oppdragstaker-avtale)': {
    en: 'Agreement on the purchase of dental services (contractor agreement)',
    sv: 'Avtal om köp av tandvårdstjänster (uppdragstagaravtal)',
    da: 'Aftale om køb af tandplejeydelser (kontrahentaftale)',
    pl: 'Umowa o zakup usług stomatologicznych (umowa z wykonawcą)',
  },
  'Avtale om leie av tilgang til tannlegevirksomhet (leietannlege-avtale)': {
    en: 'Agreement on lease of access to dental practice (associate dentist agreement)',
    sv: 'Avtal om hyra av tillgång till tandläkarverksamhet (hyrtandläkaravtal)',
    da: 'Aftale om leje af adgang til tandlægevirksomhed (lejetandlæge-aftale)',
    pl: 'Umowa najmu dostępu do gabinetu stomatologicznego (umowa z dentystą-najemcą)',
  },
  'Avtale om vikariat i fysioterapipraksis': {
    en: 'Agreement on locum position in physiotherapy practice',
    sv: 'Avtal om vikariat i fysioterapipraktik',
    da: 'Aftale om vikariat i fysioterapipraksis',
    pl: 'Umowa o zastępstwo w praktyce fizjoterapeutycznej',
  },
  Avviksbehandling: {
    en: 'Nonconformity handling',
    sv: 'Avvikelsehantering',
    da: 'Afvigelsesbehandling',
    pl: 'Obsługa niezgodności',
  },
  'Backup og tilbakekopiering': {
    en: 'Backup and restore',
    sv: 'Säkerhetskopiering och återställning',
    da: 'Backup og gendannelse',
    pl: 'Kopia zapasowa i przywracanie',
  },
  'Behandlingsavtale med forsikringsselskap/bedriftskunde': {
    en: 'Treatment agreement with insurance company/corporate client',
    sv: 'Behandlingsavtal med försäkringsbolag/företagskund',
    da: 'Behandlingsaftale med forsikringsselskab/erhvervskunde',
    pl: 'Umowa o leczenie z towarzystwem ubezpieczeniowym/klientem korporacyjnym',
  },
  'Berettiget strålebruk': {
    en: 'Justified use of radiation',
    sv: 'Berättigad strålanvändning',
    da: 'Berettiget stråleanvendelse',
    pl: 'Uzasadnione stosowanie promieniowania',
  },
  Beslutningsstøtteverktøy: {
    en: 'Decision support tool',
    sv: 'Beslutsstödsverktyg',
    da: 'Beslutningsstøtteværktøj',
    pl: 'Narzędzie wspomagania decyzji',
  },
  'Besluttede sikkerhetstiltak blir ikke gjennomført eller revidert': {
    en: 'Decided safety measures are not implemented or reviewed',
    sv: 'Beslutade säkerhetsåtgärder genomförs eller revideras inte',
    da: 'Besluttede sikkerhedstiltag gennemføres eller revideres ikke',
    pl: 'Ustalone środki bezpieczeństwa nie są wdrażane ani weryfikowane',
  },
  'Besøkende eller personale blir eksponert for smitte på grunn av mangelfull håndhygiene': {
    en: 'Visitors or staff are exposed to infection due to inadequate hand hygiene',
    sv: 'Besökare eller personal exponeras för smitta på grund av bristfällig handhygien',
    da: 'Besøgende eller personale udsættes for smitte på grund af mangelfuld håndhygiejne',
    pl: 'Odwiedzający lub personel są narażeni na zakażenie z powodu niewłaściwej higieny rąk',
  },
  'Besøkende smittes av personale med luftveissykdom': {
    en: 'Visitors are infected by staff with respiratory illness',
    sv: 'Besökare smittas av personal med luftvägssjukdom',
    da: 'Besøgende smittes af personale med luftvejssygdom',
    pl: 'Odwiedzający zostają zakażeni przez personel z chorobą układu oddechowego',
  },
  'Besøkende utsettes for koronasmitte fra andre besøkende': {
    en: 'Visitors are exposed to coronavirus infection from other visitors',
    sv: 'Besökare utsätts för coronasmitta från andra besökare',
    da: 'Besøgende udsættes for coronasmitte fra andre besøgende',
    pl: 'Odwiedzający są narażeni na zakażenie koronawirusem od innych odwiedzających',
  },
  'Bildediagnostikk ved ikke-traumatiske muskel- og skjelettlidelser': {
    en: 'Diagnostic imaging for non-traumatic musculoskeletal disorders',
    sv: 'Bilddiagnostik vid icke-traumatiska muskuloskeletala besvär',
    da: 'Billeddiagnostik ved ikke-traumatiske muskel- og skeletlidelser',
    pl: 'Diagnostyka obrazowa nieurazowych schorzeń mięśniowo-szkieletowych',
  },
  'Bildediagnostisk utredning': {
    en: 'Diagnostic imaging assessment',
    sv: 'Bilddiagnostisk utredning',
    da: 'Billeddiagnostisk udredning',
    pl: 'Diagnostyka obrazowa',
  },
  Brannvern: { en: 'Fire safety', sv: 'Brandskydd', da: 'Brandsikring', pl: 'Ochrona przeciwpożarowa' },
  'Bruk av epost': {
    en: 'Use of email',
    sv: 'Användning av e-post',
    da: 'Brug af e-mail',
    pl: 'Korzystanie z poczty elektronicznej',
  },
  'Bruk av injeksjoner innen manuellterapi': {
    en: 'Use of injections in manual therapy',
    sv: 'Användning av injektioner inom manuell terapi',
    da: 'Brug af injektioner inden for manuel terapi',
    pl: 'Stosowanie iniekcji w terapii manualnej',
  },
  'Bærbart og mobilt datautstyr': {
    en: 'Portable and mobile computer equipment',
    sv: 'Bärbar och mobil datorutrustning',
    da: 'Bærbart og mobilt computerudstyr',
    pl: 'Przenośny i mobilny sprzęt komputerowy',
  },
  'Daglig datasikkerhet for helsepersonell': {
    en: 'Daily data security for healthcare personnel',
    sv: 'Daglig datasäkerhet för vårdpersonal',
    da: 'Daglig datasikkerhed for sundhedspersonale',
    pl: 'Codzienne bezpieczeństwo danych dla personelu medycznego',
  },
  'Daglig leder': {
    en: 'Managing director',
    sv: 'Verkställande direktör',
    da: 'Daglig leder',
    pl: 'Dyrektor zarządzający',
  },
  'Daglig oversikt over fysisk fremmøtte (mal)': {
    en: 'Daily overview of persons physically present (template)',
    sv: 'Daglig översikt över fysiskt närvarande (mall)',
    da: 'Daglig oversigt over fysisk fremmødte (skabelon)',
    pl: 'Codzienny wykaz osób fizycznie obecnych (szablon)',
  },
  'Daglig personvern og datasikkerhet': {
    en: 'Daily privacy and data security',
    sv: 'Daglig integritetsskydd och datasäkerhet',
    da: 'Daglig databeskyttelse og datasikkerhed',
    pl: 'Codzienna ochrona danych osobowych i bezpieczeństwo danych',
  },
  'Dataangrep mot nettverk og server – personopplysninger kommer på avveier': {
    en: 'Cyberattack on network and server – personal data is compromised',
    sv: 'Dataangrepp mot nätverk och server – personuppgifter kommer på avvägar',
    da: 'Dataangreb mod netværk og server – personoplysninger kommer på afveje',
    pl: 'Atak na sieć i serwer – wyciek danych osobowych',
  },
  Dataansvarlig: {
    en: 'Data controller',
    sv: 'Personuppgiftsansvarig',
    da: 'Dataansvarlig',
    pl: 'Administrator danych',
  },
  Databehandleravtale: {
    en: 'Data processing agreement',
    sv: 'Personuppgiftsbiträdesavtal',
    da: 'Databehandleraftale',
    pl: 'Umowa powierzenia przetwarzania danych',
  },
  'Deling av pasientliste under koronapandemien': {
    en: 'Sharing of patient list during the coronavirus pandemic',
    sv: 'Delning av patientlista under coronapandemin',
    da: 'Deling af patientliste under coronapandemien',
    pl: 'Udostępnianie listy pacjentów podczas pandemii koronawirusa',
  },
  'Det oppstår brann i lokalene': {
    en: 'A fire breaks out on the premises',
    sv: 'Det uppstår brand i lokalerna',
    da: 'Der opstår brand i lokalerne',
    pl: 'Wybucha pożar w pomieszczeniach',
  },
  'Det oppstår varmgang eller brann i det elektriske anlegget eller elektrisk utstyr': {
    en: 'Overheating or fire occurs in the electrical installation or electrical equipment',
    sv: 'Det uppstår överhettning eller brand i den elektriska anläggningen eller elektrisk utrustning',
    da: 'Der opstår overophedning eller brand i den elektriske installation eller elektrisk udstyr',
    pl: 'Dochodzi do przegrzania lub pożaru instalacji elektrycznej lub sprzętu elektrycznego',
  },
  Driftsavtale: { en: 'Operations agreement', sv: 'Driftsavtal', da: 'Driftsaftale', pl: 'Umowa serwisowa' },
  Driftsforsikring: {
    en: 'Business interruption insurance',
    sv: 'Driftförsäkring',
    da: 'Driftsforsikring',
    pl: 'Ubezpieczenie od przerw w działalności',
  },
  'Elektromedisinsk utstyr': {
    en: 'Electromedical equipment',
    sv: 'Elektromedicinsk utrustning',
    da: 'Elektromedicinsk udstyr',
    pl: 'Sprzęt elektromedyczny',
  },
  'Elektronisk kommunikasjon med pasienter': {
    en: 'Electronic communication with patients',
    sv: 'Elektronisk kommunikation med patienter',
    da: 'Elektronisk kommunikation med patienter',
    pl: 'Komunikacja elektroniczna z pacjentami',
  },
  'Elektronisk medisinsk meldingskommunikasjon': {
    en: 'Electronic medical message communication',
    sv: 'Elektronisk medicinsk meddelandekommunikation',
    da: 'Elektronisk medicinsk meddelelseskommunikation',
    pl: 'Elektroniczna medyczna komunikacja komunikatów',
  },
  'Elektronisk signatur (PKI)': {
    en: 'Electronic signature (PKI)',
    sv: 'Elektronisk signatur (PKI)',
    da: 'Elektronisk signatur (PKI)',
    pl: 'Podpis elektroniczny (PKI)',
  },
  'Faglig avgrensning i forhold til ulike samarbeidspartnere': {
    en: 'Professional demarcation in relation to various partners',
    sv: 'Professionell avgränsning gentemot olika samarbetspartner',
    da: 'Faglig afgrænsning i forhold til forskellige samarbejdspartnere',
    pl: 'Rozgraniczenie kompetencji zawodowych wobec różnych partnerów',
  },
  'Faglig leder': {
    en: 'Professional manager',
    sv: 'Verksamhetschef',
    da: 'Faglig leder',
    pl: 'Kierownik merytoryczny',
  },
  'Faglig veileder for sykemeldere': {
    en: 'Professional guideline for sick-leave certifiers',
    sv: 'Faglig vägledning för sjukskrivande läkare',
    da: 'Faglig vejleder for sygemeldere',
    pl: 'Wytyczne zawodowe dla wystawiających zwolnienia lekarskie',
  },
  Fastlegeavtale: {
    en: 'GP agreement',
    sv: 'Avtal om fast läkarkontakt',
    da: 'Almen praksis-aftale',
    pl: 'Umowa lekarza rodzinnego',
  },
  'Fjernaksess/online support': {
    en: 'Remote access/online support',
    sv: 'Fjärråtkomst/onlinesupport',
    da: 'Fjernadgang/onlinesupport',
    pl: 'Zdalny dostęp/wsparcie online',
  },
  Forbedringsprosjekt: {
    en: 'Improvement project',
    sv: 'Förbättringsprojekt',
    da: 'Forbedringsprojekt',
    pl: 'Projekt usprawnień',
  },
  'Forholdet til legemiddelindustrien': {
    en: 'Relationship with the pharmaceutical industry',
    sv: 'Förhållandet till läkemedelsindustrin',
    da: 'Forholdet til lægemiddelindustrien',
    pl: 'Relacje z przemysłem farmaceutycznym',
  },
  Forskrifter: { en: 'Regulations', sv: 'Föreskrifter', da: 'Forskrifter', pl: 'Rozporządzenia' },
  'Forsvarlig bruk av røntgen for ulike pasientgrupper': {
    en: 'Responsible use of X-ray for different patient groups',
    sv: 'Försvarlig användning av röntgen för olika patientgrupper',
    da: 'Forsvarlig brug af røntgen for forskellige patientgrupper',
    pl: 'Odpowiedzialne stosowanie promieni rentgenowskich u różnych grup pacjentów',
  },
  'Frivillig yrkesskadetrygd': {
    en: 'Voluntary occupational injury insurance',
    sv: 'Frivillig yrkesskadeförsäkring',
    da: 'Frivillig arbejdsskadeforsikring',
    pl: 'Dobrowolne ubezpieczenie od wypadków przy pracy',
  },
  Fysioterapeut: { en: 'Physiotherapist', sv: 'Fysioterapeut', da: 'Fysioterapeut', pl: 'Fizjoterapeuta' },
  'Fysisk sikring av områder og utstyr': {
    en: 'Physical security of areas and equipment',
    sv: 'Fysisk säkring av områden och utrustning',
    da: 'Fysisk sikring af områder og udstyr',
    pl: 'Fizyczne zabezpieczenie obszarów i sprzętu',
  },
  Generalforsamling: { en: 'General meeting', sv: 'Bolagsstämma', da: 'Generalforsamling', pl: 'Walne zgromadzenie' },
  'Generell undersøkelse i manuellterapi': {
    en: 'General examination in manual therapy',
    sv: 'Allmän undersökning i manuell terapi',
    da: 'Generel undersøgelse i manuel terapi',
    pl: 'Ogólne badanie w terapii manualnej',
  },
  'Grenseoppgang mellom profesjonene på tannlegekontoret': {
    en: 'Demarcation between the professions at the dental office',
    sv: 'Gränsdragning mellan professionerna på tandläkarkontoret',
    da: 'Grænsedragning mellem professionerne på tandlægekontoret',
    pl: 'Rozgraniczenie kompetencji między zawodami w gabinecie stomatologicznym',
  },
  'HMS-ansvarlig': {
    en: 'HSE manager',
    sv: 'Arbetsmiljöansvarig',
    da: 'Arbejdsmiljøansvarlig',
    pl: 'Osoba odpowiedzialna za BHP',
  },
  'HMS-gjennomgang': { en: 'HSE review', sv: 'Arbetsmiljögenomgång', da: 'Arbejdsmiljøgennemgang', pl: 'Przegląd BHP' },
  'HMS-handlingsplan': {
    en: 'HSE action plan',
    sv: 'Arbetsmiljöhandlingsplan',
    da: 'Arbejdsmiljøhandlingsplan',
    pl: 'Plan działań BHP',
  },
  'HMS-plan': { en: 'HSE plan', sv: 'Arbetsmiljöplan', da: 'Arbejdsmiljøplan', pl: 'Plan BHP' },
  'Helsepersonell snoker i pasientdata': {
    en: 'Health personnel snooping in patient data',
    sv: 'Vårdpersonal snokar i patientdata',
    da: 'Sundhedspersonale snager i patientdata',
    pl: 'Personel medyczny przegląda dane pacjentów bez uprawnień',
  },
  'Henvisning til fysioterapi': {
    en: 'Referral to physiotherapy',
    sv: 'Remiss till fysioterapi',
    da: 'Henvisning til fysioterapi',
    pl: 'Skierowanie na fizjoterapię',
  },
  'Henvisning til spesialist (revmatolog, nevrolog og ortoped)': {
    en: 'Referral to specialist (rheumatologist, neurologist and orthopaedist)',
    sv: 'Remiss till specialist (reumatolog, neurolog och ortoped)',
    da: 'Henvisning til speciallæge (reumatolog, neurolog og ortopæd)',
    pl: 'Skierowanie do specjalisty (reumatolog, neurolog i ortopeda)',
  },
  Hjemmekontor: { en: 'Home office', sv: 'Hemmakontor', da: 'Hjemmekontor', pl: 'Praca zdalna' },
  Husleieavtale: { en: 'Lease agreement', sv: 'Hyresavtal', da: 'Lejeaftale', pl: 'Umowa najmu' },
  Hverdagsøkonomi: { en: 'Everyday finances', sv: 'Vardagsekonomi', da: 'Hverdagsøkonomi', pl: 'Finanse codzienne' },
  'Håndtering av pasientinformasjon for pasientansvarlig i fravær': {
    en: 'Handling of patient information for the patient-responsible person during absence',
    sv: 'Hantering av patientinformation för patientansvarig vid frånvaro',
    da: 'Håndtering af patientinformation for patientansvarlig ved fravær',
    pl: 'Postępowanie z informacjami o pacjencie podczas nieobecności osoby odpowiedzialnej za pacjenta',
  },
  'IKT-prosjekter': { en: 'ICT projects', sv: 'IKT-projekt', da: 'IKT-projekter', pl: 'Projekty ICT' },
  'Individuell avtale for avtalespesialister': {
    en: 'Individual agreement for contracted specialists',
    sv: 'Individuellt avtal för avtalsspecialister',
    da: 'Individuel aftale for aftalespecialister',
    pl: 'Indywidualna umowa dla specjalistów kontraktowych',
  },
  'Individuell avtale om privat praksis i fysioterapi (avtale om driftstilskudd)': {
    en: 'Individual agreement on private physiotherapy practice (operating subsidy agreement)',
    sv: 'Individuellt avtal om privat praktik inom fysioterapi (avtal om driftsbidrag)',
    da: 'Individuel aftale om privat praksis i fysioterapi (aftale om driftstilskud)',
    pl: 'Indywidualna umowa o prywatnej praktyce fizjoterapeutycznej (umowa o dotacji operacyjnej)',
  },
  'Individuell/personlig avtale om regional avtalepraksis for psykologspesialist': {
    en: 'Individual/personal agreement on regional contracted practice for specialist psychologists',
    sv: 'Individuellt/personligt avtal om regional avtalspraktik för psykologspecialist',
    da: 'Individuel/personlig aftale om regional aftalepraksis for psykologspecialist',
    pl: 'Indywidualna/osobista umowa o regionalnej praktyce kontraktowej dla psychologa specjalisty',
  },
  'Info til pasienter om personvern og journalrettigheter': {
    en: 'Information for patients on privacy and medical record rights',
    sv: 'Information till patienter om integritet och journalrättigheter',
    da: 'Information til patienter om privatliv og journalrettigheder',
    pl: 'Informacja dla pacjentów o ochronie prywatności i prawach do dokumentacji medycznej',
  },
  'Info til pasienter om rett til valg av behandlingssted': {
    en: 'Information for patients on the right to choose treatment location',
    sv: 'Information till patienter om rätten att välja behandlingsplats',
    da: 'Information til patienter om retten til valg af behandlingssted',
    pl: 'Informacja dla pacjentów o prawie wyboru miejsca leczenia',
  },
  'Informasjon om rett til å søke dispensasjon eller klage på sjømannslegens vedtak': {
    en: "Information on the right to apply for exemption or appeal the seafarer's doctor's decision",
    sv: 'Information om rätten att ansöka om dispens eller överklaga sjömansläkarens beslut',
    da: 'Information om retten til at søge dispensation eller klage over søfartslægens afgørelse',
    pl: 'Informacja o prawie do ubiegania się o zwolnienie lub odwołania od decyzji lekarza marynarki',
  },
  'Informasjon til pasient ved bruk av videokonsultasjon': {
    en: 'Information for patients regarding the use of video consultation',
    sv: 'Information till patient vid användning av videokonsultation',
    da: 'Information til patient ved brug af videokonsultation',
    pl: 'Informacja dla pacjenta dotycząca korzystania z konsultacji wideo',
  },
  'Informasjonsskriv til nye pasienter': {
    en: 'Information letter for new patients',
    sv: 'Informationsbrev till nya patienter',
    da: 'Informationsskrivelse til nye patienter',
    pl: 'Pismo informacyjne dla nowych pacjentów',
  },
  'Innkommende melding avvises fordi pasienten ikke er registrert i våre fastlegelister': {
    en: 'Incoming message rejected because the patient is not registered on our GP lists',
    sv: 'Inkommande meddelande avvisas eftersom patienten inte är registrerad i våra listor för fast läkarkontakt',
    da: 'Indkommende besked afvises, fordi patienten ikke er registreret på vores lister for fast læge',
    pl: 'Wiadomość przychodząca odrzucona, ponieważ pacjent nie jest zarejestrowany na naszych listach lekarzy rodzinnych',
  },
  'Innredning og drift av manuellterapiklinikk': {
    en: 'Furnishing and operation of a manual therapy clinic',
    sv: 'Inredning och drift av manuellterapiklinik',
    da: 'Indretning og drift af manuelterapiklinik',
    pl: 'Wyposażenie i prowadzenie kliniki terapii manualnej',
  },
  'Internavtale for gruppepraksis': {
    en: 'Internal agreement for group practice',
    sv: 'Internt avtal för grupppraktik',
    da: 'Intern aftale for gruppepraksis',
    pl: 'Umowa wewnętrzna dla praktyki grupowej',
  },
  'Intervjuguide for medarbeidersamtaler': {
    en: 'Interview guide for performance appraisals',
    sv: 'Intervjuguide för medarbetarsamtal',
    da: 'Interviewguide til medarbejdersamtaler',
    pl: 'Przewodnik po rozmowach oceniających z pracownikami',
  },
  'Journalføring for tannleger og medarbeidere': {
    en: 'Medical record keeping for dentists and staff',
    sv: 'Journalföring för tandläkare och medarbetare',
    da: 'Journalføring for tandlæger og medarbejdere',
    pl: 'Prowadzenie dokumentacji medycznej dla dentystów i pracowników',
  },
  'Kartlegging av helse-, miljø- og sikkerhetsforhold': {
    en: 'Assessment of health, safety and environmental conditions',
    sv: 'Kartläggning av hälso-, miljö- och säkerhetsförhållanden',
    da: 'Kortlægning af sundheds-, miljø- og sikkerhedsforhold',
    pl: 'Ocena warunków bezpieczeństwa, higieny pracy i środowiska (BHP)',
  },
  Kiropraktor: { en: 'Chiropractor', sv: 'Kiropraktor', da: 'Kiropraktor', pl: 'Kręgarz' },
  'Klassifisering av medisinsk utstyr for rengjøring': {
    en: 'Classification of medical devices for cleaning',
    sv: 'Klassificering av medicinsk utrustning för rengöring',
    da: 'Klassificering af medicinsk udstyr til rengøring',
    pl: 'Klasyfikacja wyrobów medycznych do czyszczenia',
  },
  'Klinisk undersøkelse av muskel og skjelettsystemet': {
    en: 'Clinical examination of the musculoskeletal system',
    sv: 'Klinisk undersökning av muskuloskeletala systemet',
    da: 'Klinisk undersøgelse af muskel- og skeletsystemet',
    pl: 'Badanie kliniczne układu mięśniowo-szkieletowego',
  },
  'Kommunikasjon og markedsføring via sosiale medier': {
    en: 'Communication and marketing via social media',
    sv: 'Kommunikation och marknadsföring via sociala medier',
    da: 'Kommunikation og markedsføring via sociale medier',
    pl: 'Komunikacja i marketing za pośrednictwem mediów społecznościowych',
  },
  'Kommunikasjonspartnere for elektroniske meldinger': {
    en: 'Communication partners for electronic messages',
    sv: 'Kommunikationspartner för elektroniska meddelanden',
    da: 'Kommunikationspartnere for elektroniske beskeder',
    pl: 'Partnerzy komunikacyjni dla wiadomości elektronicznych',
  },
  'Kommunikasjonsstrategi for kiropraktorer': {
    en: 'Communication strategy for chiropractors',
    sv: 'Kommunikationsstrategi för kiropraktorer',
    da: 'Kommunikationsstrategi for kiropraktorer',
    pl: 'Strategia komunikacji dla kręgarzy',
  },
  'Kontakt med media': {
    en: 'Contact with the media',
    sv: 'Kontakt med media',
    da: 'Kontakt med medierne',
    pl: 'Kontakt z mediami',
  },
  'Kontroll av brannvern': {
    en: 'Inspection of fire safety',
    sv: 'Kontroll av brandskydd',
    da: 'Kontrol af brandsikring',
    pl: 'Kontrola ochrony przeciwpożarowej',
  },
  'Kontroll av elektriske installasjoner': {
    en: 'Inspection of electrical installations',
    sv: 'Kontroll av elektriska installationer',
    da: 'Kontrol af elektriske installationer',
    pl: 'Kontrola instalacji elektrycznych',
  },
  'Korsryggsmerter – med og uten nerverotaffeksjon': {
    en: 'Low back pain – with and without nerve root involvement',
    sv: 'Ländryggssmärta – med och utan nervrotspåverkan',
    da: 'Lændesmerter – med og uden nerverodspåvirkning',
    pl: 'Ból krzyża – z zajęciem korzeni nerwowych i bez',
  },
  'Krav til oppvaskmaskin for medisinsk utstyr': {
    en: 'Requirements for washer-disinfector for medical devices',
    sv: 'Krav på diskmaskin för medicinsk utrustning',
    da: 'Krav til opvaskemaskine til medicinsk udstyr',
    pl: 'Wymagania dla myjni-dezynfektora do wyrobów medycznych',
  },
  Kvalitetsansvarlig: {
    en: 'Quality manager',
    sv: 'Kvalitetsansvarig',
    da: 'Kvalitetsansvarlig',
    pl: 'Kierownik ds. jakości',
  },
  Laboratorium: { en: 'Laboratory', sv: 'Laboratorium', da: 'Laboratorium', pl: 'Laboratorium' },
  'Lagringstid og sletting av helse- og personopplysninger': {
    en: 'Retention period and deletion of health and personal data',
    sv: 'Lagringstid och radering av hälso- och personuppgifter',
    da: 'Opbevaringstid og sletning af helbreds- og personoplysninger',
    pl: 'Okres przechowywania i usuwanie danych o zdrowiu i danych osobowych',
  },
  'Ledelsens gjennomgang av internkontrollen': {
    en: 'Management review of internal control',
    sv: 'Ledningens genomgång av internkontrollen',
    da: 'Ledelsens gennemgang af den interne kontrol',
    pl: 'Przegląd kontroli wewnętrznej przez kierownictwo',
  },
  Lege: { en: 'Physician', sv: 'Läkare', da: 'Læge', pl: 'Lekarz' },
  'Leverandør utsetter personopplysninger for sikkerhetsrisiko': {
    en: 'Supplier exposes personal data to a security risk',
    sv: 'Leverantör utsätter personuppgifter för säkerhetsrisk',
    da: 'Leverandør udsætter personoplysninger for sikkerhedsrisiko',
    pl: 'Dostawca naraża dane osobowe na ryzyko bezpieczeństwa',
  },
  Lover: { en: 'Laws', sv: 'Lagar', da: 'Love', pl: 'Ustawy' },
  'Mal for medarbeidersamtaler': {
    en: 'Template for performance appraisals',
    sv: 'Mall för medarbetarsamtal',
    da: 'Skabelon til medarbejdersamtaler',
    pl: 'Szablon rozmów oceniających z pracownikami',
  },
  'Mal for registrering av sykefravær': {
    en: 'Template for recording sickness absence',
    sv: 'Mall för registrering av sjukfrånvaro',
    da: 'Skabelon til registrering af sygefravær',
    pl: 'Szablon rejestracji nieobecności chorobowych',
  },
  'Manglende opplæring i personvern, informasjonssikkerhet og avviksrapportering': {
    en: 'Lack of training in data protection, information security and deviation reporting',
    sv: 'Bristande utbildning i dataskydd, informationssäkerhet och avvikelserapportering',
    da: 'Manglende oplæring i databeskyttelse, informationssikkerhed og afvigelsesrapportering',
    pl: 'Brak szkolenia w zakresie ochrony danych, bezpieczeństwa informacji i zgłaszania niezgodności',
  },
  Manuellterapeut: { en: 'Manual therapist', sv: 'Manuellterapeut', da: 'Manuel terapeut', pl: 'Terapeuta manualny' },
  Medarbeidersamtale: {
    en: 'Performance appraisal',
    sv: 'Medarbetarsamtal',
    da: 'Medarbejdersamtale',
    pl: 'Rozmowa oceniająca z pracownikiem',
  },
  'Medikamenter og medisinsk forbruksutstyr': {
    en: 'Medicines and medical consumables',
    sv: 'Läkemedel och medicinskt förbrukningsmaterial',
    da: 'Medicin og medicinske forbrugsvarer',
    pl: 'Leki i medyczne materiały zużywalne',
  },
  'Medisinskfaglig ansvarlig for strålebruk': {
    en: 'Medical officer responsible for the use of radiation',
    sv: 'Medicinskt ansvarig för strålningsanvändning',
    da: 'Medicinskfagligt ansvarlig for strålebrug',
    pl: 'Osoba odpowiedzialna medycznie za stosowanie promieniowania',
  },
  'Melding blir ikke sendt på nytt etter svikt i første sending': {
    en: 'Message is not resent after failure of the first transmission',
    sv: 'Meddelande skickas inte på nytt efter fel vid första sändningen',
    da: 'Meddelelse sendes ikke igen efter fejl ved første afsendelse',
    pl: 'Komunikat nie zostaje wysłany ponownie po niepowodzeniu pierwszej wysyłki',
  },
  'Melding kommer ikke frem pga svikt i mottakers meldingshåndtering': {
    en: "Message does not arrive due to failure in recipient's message handling",
    sv: 'Meddelande når inte fram på grund av fel i mottagarens meddelandehantering',
    da: 'Besked når ikke frem på grund af fejl i modtagerens beskedhåndtering',
    pl: 'Wiadomość nie dociera z powodu awarii obsługi wiadomości u odbiorcy',
  },
  'Melding til statsforvalteren – helsekrav ikke oppfylt for førerkort': {
    en: 'Notification to the County Governor – health requirements for driving licence not met',
    sv: 'Anmälan till länsstyrelsen – hälsokrav för körkort inte uppfyllda',
    da: 'Indberetning til statsforvalteren – helbredskrav til kørekort ikke opfyldt',
    pl: 'Zgłoszenie do wojewody – wymogi zdrowotne dla prawa jazdy niespełnione',
  },
  'Meldinger blir ikke vurdert når kollega er fraværende': {
    en: 'Messages are not assessed when a colleague is absent',
    sv: 'Meddelanden bedöms inte när en kollega är frånvarande',
    da: 'Beskeder vurderes ikke, når en kollega er fraværende',
    pl: 'Wiadomości nie są oceniane podczas nieobecności współpracownika',
  },
  'Meldinger blir ikke vurdert ved fravær': {
    en: 'Messages are not assessed during absence',
    sv: 'Meddelanden bedöms inte vid frånvaro',
    da: 'Beskeder vurderes ikke ved fravær',
    pl: 'Wiadomości nie są oceniane podczas nieobecności',
  },
  'Meldinger kan ikke sendes og mottas fordi nettforbindelsen er nede': {
    en: 'Messages cannot be sent and received because the network connection is down',
    sv: 'Meddelanden kan inte skickas och tas emot eftersom nätverksanslutningen är nere',
    da: 'Beskeder kan ikke sendes og modtages, fordi netforbindelsen er nede',
    pl: 'Nie można wysyłać i odbierać wiadomości, ponieważ połączenie sieciowe jest niedostępne',
  },
  'Meldingsovervåking blir ikke utført': {
    en: 'Message monitoring is not carried out',
    sv: 'Meddelandeövervakning utförs inte',
    da: 'Beskedovervågning udføres ikke',
    pl: 'Monitorowanie wiadomości nie jest wykonywane',
  },
  'Mottaker varsles ikke når vi har funnet en negativ applikasjonskvittering': {
    en: 'The recipient is not notified when we have found a negative application receipt',
    sv: 'Mottagaren meddelas inte när vi har funnit en negativ applikationskvittens',
    da: 'Modtageren underrettes ikke, når vi har fundet en negativ applikationskvittering',
    pl: 'Odbiorca nie jest powiadamiany, gdy znaleziono negatywne potwierdzenie aplikacji',
  },
  'Mål for HMS-arbeidet': {
    en: 'Objectives for the HSE work',
    sv: 'Mål för HMS-arbetet',
    da: 'Mål for arbejdsmiljøarbejdet',
    pl: 'Cele pracy w zakresie BHP',
  },
  'Mål for datasikkerhetsarbeidet': {
    en: 'Objectives for the data security work',
    sv: 'Mål för datasäkerhetsarbetet',
    da: 'Mål for datasikkerhedsarbejdet',
    pl: 'Cele pracy w zakresie bezpieczeństwa danych',
  },
  'Mål for kvalitetsarbeidet': {
    en: 'Objectives for the quality work',
    sv: 'Mål för kvalitetsarbetet',
    da: 'Mål for kvalitetsarbejdet',
    pl: 'Cele pracy w zakresie jakości',
  },
  'Mål for sjømannslegen': {
    en: "Objectives for the seafarer's doctor",
    sv: 'Mål för sjömansläkaren',
    da: 'Mål for søfartslægen',
    pl: 'Cele lekarza marynarzy',
  },
  'Mål for virksomheten': {
    en: 'Objectives for the organisation',
    sv: 'Mål för verksamheten',
    da: 'Mål for virksomheden',
    pl: 'Cele organizacji',
  },
  Møte: { en: 'Meeting', sv: 'Möte', da: 'Møde', pl: 'Spotkanie' },
  Møteplan: { en: 'Meeting plan', sv: 'Mötesplan', da: 'Mødeplan', pl: 'Plan spotkań' },
  Naprapat: { en: 'Naprapath', sv: 'Naprapat', da: 'Naprapat', pl: 'Naprapata' },
  'Negativ applikasjonskvittering blir ikke oppdaget av vårt kontor': {
    en: 'Negative application receipt is not detected by our office',
    sv: 'Negativ applikationskvittens upptäcks inte av vårt kontor',
    da: 'Negativ applikationskvittering opdages ikke af vores kontor',
    pl: 'Negatywne potwierdzenie aplikacji nie zostaje wykryte przez nasze biuro',
  },
  'Omsorgsdager (sykt barn)': {
    en: 'Care days (sick child)',
    sv: 'Vård av barn (sjukt barn)',
    da: 'Omsorgsdage (sygt barn)',
    pl: 'Dni opieki (chore dziecko)',
  },
  'Oppbevaring og bruk av PKI-smartkort': {
    en: 'Storage and use of PKI smart cards',
    sv: 'Förvaring och användning av PKI-smartkort',
    da: 'Opbevaring og brug af PKI-smartkort',
    pl: 'Przechowywanie i używanie kart inteligentnych PKI',
  },
  'Oppfølging av røntgenapparat på kiropraktorklinikker': {
    en: 'Follow-up of X-ray equipment at chiropractic clinics',
    sv: 'Uppföljning av röntgenutrustning på kiropraktorkliniker',
    da: 'Opfølgning af røntgenapparat på kiropraktorklinikker',
    pl: 'Nadzór nad aparatem rentgenowskim w klinikach chiropraktyki',
  },
  Opplæringsansvarlig: {
    en: 'Training coordinator',
    sv: 'Utbildningsansvarig',
    da: 'Uddannelsesansvarlig',
    pl: 'Osoba odpowiedzialna za szkolenia',
  },
  'Optimalisert strålebruk': {
    en: 'Optimised use of radiation',
    sv: 'Optimerad strålanvändning',
    da: 'Optimeret strålingsbrug',
    pl: 'Zoptymalizowane stosowanie promieniowania',
  },
  'Organisering av samarbeid mellom tannleger': {
    en: 'Organising collaboration between dentists',
    sv: 'Organisering av samarbete mellan tandläkare',
    da: 'Organisering af samarbejde mellem tandlæger',
    pl: 'Organizacja współpracy między dentystami',
  },
  Osteopat: { en: 'Osteopath', sv: 'Osteopat', da: 'Osteopat', pl: 'Osteopata' },
  'Oversikt over sjømannslegens systemer og dokumentasjon': {
    en: "Overview of the seafarer's doctor's systems and documentation",
    sv: 'Översikt över sjömansläkarens system och dokumentation',
    da: 'Oversigt over søfartslægens systemer og dokumentation',
    pl: 'Przegląd systemów i dokumentacji lekarza marynarzy',
  },
  'Pasient blir eksponert for smitte fra tannteknisk arbeider': {
    en: 'Patient is exposed to infection from dental technical work',
    sv: 'Patient exponeras för smitta från tandtekniskt arbete',
    da: 'Patient eksponeres for smitte fra tandteknisk arbejde',
    pl: 'Pacjent zostaje narażony na zakażenie od prac technicznych dentystycznych',
  },
  'Pasient eller personale blir eksponert for smitte i pasientmottak eller venterom': {
    en: 'Patient or staff is exposed to infection in reception or waiting room',
    sv: 'Patient eller personal exponeras för smitta i patientmottagning eller väntrum',
    da: 'Patient eller personale eksponeres for smitte i patientmodtagelse eller venteværelse',
    pl: 'Pacjent lub personel zostaje narażony na zakażenie w rejestracji lub poczekalni',
  },
  'Pasient eller personale blir eksponert for smitte på grunn av mangelfull håndhygiene': {
    en: 'Patient or staff is exposed to infection due to inadequate hand hygiene',
    sv: 'Patient eller personal exponeras för smitta på grund av bristfällig handhygien',
    da: 'Patient eller personale eksponeres for smitte på grund af mangelfuld håndhygiejne',
    pl: 'Pacjent lub personel zostaje narażony na zakażenie z powodu niedostatecznej higieny rąk',
  },
  'Pasient eller personale blir eksponert for smitte på grunn av mangelfull regjøring av utstyr': {
    en: 'Patient or staff is exposed to infection due to inadequate cleaning of equipment',
    sv: 'Patient eller personal exponeras för smitta på grund av bristfällig rengöring av utrustning',
    da: 'Patient eller personale eksponeres for smitte på grund af mangelfuld rengøring af udstyr',
    pl: 'Pacjent lub personel zostaje narażony na zakażenie z powodu niedostatecznego czyszczenia sprzętu',
  },
  'Pasient smittes av annen pasient med luftveissykdom': {
    en: 'Patient is infected by another patient with a respiratory illness',
    sv: 'Patient smittas av annan patient med luftvägssjukdom',
    da: 'Patient smittes af anden patient med luftvejssygdom',
    pl: 'Pacjent zostaje zakażony przez innego pacjenta z chorobą układu oddechowego',
  },
  'Pasient smittes av personale med luftveissykdom': {
    en: 'Patient is infected by staff with a respiratory illness',
    sv: 'Patient smittas av personal med luftvägssjukdom',
    da: 'Patient smittes af personale med luftvejssygdom',
    pl: 'Pacjent zostaje zakażony przez personel z chorobą układu oddechowego',
  },
  'Pasientdata ikke tilgjengelig pga stans i server eller manglende tilgang til denne': {
    en: 'Patient data unavailable due to server outage or lack of access to it',
    sv: 'Patientdata inte tillgängliga på grund av serverstopp eller utebliven åtkomst till denna',
    da: 'Patientdata ikke tilgængelige på grund af serverstop eller manglende adgang til denne',
    pl: 'Dane pacjenta niedostępne z powodu awarii serwera lub braku dostępu do niego',
  },
  'Pasientdata leveres til feil mottaker': {
    en: 'Patient data is delivered to the wrong recipient',
    sv: 'Patientdata levereras till fel mottagare',
    da: 'Patientdata leveres til forkert modtager',
    pl: 'Dane pacjenta zostają dostarczone do niewłaściwego odbiorcy',
  },
  Pasientmottak: {
    en: 'Patient reception',
    sv: 'Patientmottagning',
    da: 'Patientmodtagelse',
    pl: 'Rejestracja pacjentów',
  },
  'Passord og tilgangsstyring': {
    en: 'Passwords and access control',
    sv: 'Lösenord och åtkomststyrning',
    da: 'Adgangskoder og adgangsstyring',
    pl: 'Hasła i zarządzanie dostępem',
  },
  Pensjonsforsikring: {
    en: 'Pension insurance',
    sv: 'Pensionsförsäkring',
    da: 'Pensionsforsikring',
    pl: 'Ubezpieczenie emerytalne',
  },
  Personalansvarlig: {
    en: 'Personnel manager',
    sv: 'Personalansvarig',
    da: 'Personaleansvarlig',
    pl: 'Kierownik ds. personelu',
  },
  'Personale blir eksponert for smitte på grunn av manglende bruk av beskyttelsesutstyr': {
    en: 'Staff is exposed to infection due to failure to use protective equipment',
    sv: 'Personal exponeras för smitta på grund av utebliven användning av skyddsutrustning',
    da: 'Personale eksponeres for smitte på grund af manglende brug af beskyttelsesudstyr',
    pl: 'Personel zostaje narażony na zakażenie z powodu niestosowania środków ochrony',
  },
  'Personale blir syk eller skadet på grunn av feilbelastning': {
    en: 'Staff becomes ill or injured due to incorrect strain',
    sv: 'Personal blir sjuk eller skadad på grund av felbelastning',
    da: 'Personale bliver syg eller skadet på grund af fejlbelastning',
    pl: 'Personel choruje lub doznaje urazu z powodu nieprawidłowego obciążenia',
  },
  'Personale blir syk eller skadet på grunn av kontakt med biologisk materiale': {
    en: 'Staff becomes ill or injured due to contact with biological material',
    sv: 'Personal blir sjuk eller skadad på grund av kontakt med biologiskt material',
    da: 'Personale bliver syg eller skadet på grund af kontakt med biologisk materiale',
    pl: 'Personel choruje lub doznaje urazu z powodu kontaktu z materiałem biologicznym',
  },
  'Personale blir syk eller skadet på grunn av kontakt med farlige stoffer': {
    en: 'Staff becomes ill or injured due to contact with hazardous substances',
    sv: 'Personal blir sjuk eller skadad på grund av kontakt med farliga ämnen',
    da: 'Personale bliver syg eller skadet på grund af kontakt med farlige stoffer',
    pl: 'Personel choruje lub doznaje urazu z powodu kontaktu z substancjami niebezpiecznymi',
  },
  'Personale blir syke på grunn av stress': {
    en: 'Staff become ill due to stress',
    sv: 'Personal blir sjuka på grund av stress',
    da: 'Personale bliver syge på grund af stress',
    pl: 'Personel choruje z powodu stresu',
  },
  'Personale blir utsatt for mobbing eller trakassering': {
    en: 'Staff are subjected to bullying or harassment',
    sv: 'Personal utsätts för mobbning eller trakasserier',
    da: 'Personale udsættes for mobning eller chikane',
    pl: 'Personel jest narażony na mobbing lub molestowanie',
  },
  'Personale blir utsatt for skade ved bruk av vibrerende utstyr': {
    en: 'Staff are injured by the use of vibrating equipment',
    sv: 'Personal skadas vid användning av vibrerande utrustning',
    da: 'Personale skades ved brug af vibrerende udstyr',
    pl: 'Personel doznaje urazu przy użyciu sprzętu wibracyjnego',
  },
  'Personale får helseplager på grunn av dårlig inneklima': {
    en: 'Staff develop health problems due to poor indoor air quality',
    sv: 'Personal får hälsobesvär på grund av dåligt inomhusklimat',
    da: 'Personale får helbredsgener på grund af dårligt indeklima',
    pl: 'Personel doświadcza dolegliwości zdrowotnych z powodu złego klimatu wewnętrznego',
  },
  'Personale får hudlidelser på grunn av vått arbeid': {
    en: 'Staff develop skin disorders due to wet work',
    sv: 'Personal får hudbesvär på grund av vått arbete',
    da: 'Personale får hudlidelser på grund af vådt arbejde',
    pl: 'Personel doświadcza schorzeń skóry z powodu pracy w warunkach wilgotnych',
  },
  'Personale får problemer med rus eller spilleavhengighet': {
    en: 'Staff develop substance abuse or gambling addiction problems',
    sv: 'Personal får problem med missbruk eller spelberoende',
    da: 'Personale får problemer med misbrug eller ludomani',
    pl: 'Personel boryka się z problemami uzależnienia od substancji lub hazardu',
  },
  'Personale får synsrelaterte plager på grunn av ergonomiske forhold': {
    en: 'Staff develop vision-related problems due to ergonomic conditions',
    sv: 'Personal får synrelaterade besvär på grund av ergonomiska förhållanden',
    da: 'Personale får synsrelaterede gener på grund af ergonomiske forhold',
    pl: 'Personel doświadcza dolegliwości związanych ze wzrokiem z powodu warunków ergonomicznych',
  },
  'Personale smittes av besøkende med koronavirus': {
    en: 'Staff are infected with coronavirus by visitors',
    sv: 'Personal smittas av besökare med coronavirus',
    da: 'Personale smittes af besøgende med coronavirus',
    pl: 'Personel zaraża się koronawirusem od odwiedzających',
  },
  'Personale smittes av pasient med luftveissykdom': {
    en: 'Staff are infected by a patient with a respiratory illness',
    sv: 'Personal smittas av patient med luftvägssjukdom',
    da: 'Personale smittes af patient med luftvejssygdom',
    pl: 'Personel zaraża się od pacjenta z chorobą układu oddechowego',
  },
  'Personale snoker i persondata': {
    en: 'Staff snoop in personal data',
    sv: 'Personal snokar i personuppgifter',
    da: 'Personale snager i persondata',
    pl: 'Personel przegląda dane osobowe bez upoważnienia',
  },
  'Personale stikker seg på kanyle som er brukt på pasient': {
    en: 'Staff sustain a needlestick injury from a needle used on a patient',
    sv: 'Personal sticker sig på kanyl som använts på patient',
    da: 'Personale stikker sig på kanyle, der er brugt på en patient',
    pl: 'Personel kłuje się igłą użytą u pacjenta',
  },
  'Personale utsettes for overbelastning eller sosial isolasjon ved alenearbeid': {
    en: 'Staff are exposed to overload or social isolation when working alone',
    sv: 'Personal utsätts för överbelastning eller social isolering vid ensamarbete',
    da: 'Personale udsættes for overbelastning eller social isolation ved alenearbejde',
    pl: 'Personel jest narażony na przeciążenie lub izolację społeczną podczas pracy w pojedynkę',
  },
  'Personale utsettes for støyskader': {
    en: 'Staff are exposed to noise-induced injuries',
    sv: 'Personal utsätts för bullerskador',
    da: 'Personale udsættes for støjskader',
    pl: 'Personel jest narażony na uszkodzenia słuchu spowodowane hałasem',
  },
  'Personale utsettes for uønsket seksuell oppmerksomhet': {
    en: 'Staff are subjected to unwanted sexual attention',
    sv: 'Personal utsätts för oönskad sexuell uppmärksamhet',
    da: 'Personale udsættes for uønsket seksuel opmærksomhed',
    pl: 'Personel jest narażony na niepożądane zainteresowanie seksualne',
  },
  'Personale utsettes for vold eller trusler av besøkende': {
    en: 'Staff are subjected to violence or threats by visitors',
    sv: 'Personal utsätts för våld eller hot av besökare',
    da: 'Personale udsættes for vold eller trusler af besøgende',
    pl: 'Personel jest narażony na przemoc lub groźby ze strony odwiedzających',
  },
  Personalmøte: { en: 'Staff meeting', sv: 'Personalmöte', da: 'Personalemøde', pl: 'Zebranie personelu' },
  'Personell leser, endrer og sletter mer pasientdata enn de har lov til': {
    en: 'Personnel read, modify and delete more patient data than they are authorised to',
    sv: 'Personal läser, ändrar och raderar mer patientdata än de har rätt till',
    da: 'Personale læser, ændrer og sletter mere patientdata, end de har tilladelse til',
    pl: 'Personel odczytuje, modyfikuje i usuwa więcej danych pacjentów, niż jest do tego uprawniony',
  },
  'Personvernerklæring for personalet i forbindelse med koronapandemien': {
    en: 'Privacy statement for staff in connection with the coronavirus pandemic',
    sv: 'Integritetspolicy för personalen i samband med coronapandemin',
    da: 'Privatlivspolitik for personalet i forbindelse med coronapandemien',
    pl: 'Oświadczenie o ochronie prywatności dla personelu w związku z pandemią koronawirusa',
  },
  'Planlagt fravær': {
    en: 'Planned absence',
    sv: 'Planerad frånvaro',
    da: 'Planlagt fravær',
    pl: 'Planowana nieobecność',
  },
  Praksismøte: { en: 'Practice meeting', sv: 'Praktikmöte', da: 'Praksismøde', pl: 'Zebranie praktyki' },
  Psykolog: { en: 'Psychologist', sv: 'Psykolog', da: 'Psykolog', pl: 'Psycholog' },
  Regelverk: { en: 'Regulations', sv: 'Regelverk', da: 'Regelværk', pl: 'Przepisy' },
  'Reisevirksomhet for helsepersonell under koronapandemien': {
    en: 'Travel activity for healthcare personnel during the coronavirus pandemic',
    sv: 'Resor för vårdpersonal under coronapandemin',
    da: 'Rejseaktivitet for sundhedspersonale under coronapandemien',
    pl: 'Podróże personelu medycznego w czasie pandemii koronawirusa',
  },
  Renholder: { en: 'Cleaner', sv: 'Lokalvårdare', da: 'Rengøringsassistent', pl: 'Sprzątacz' },
  'Retningslinjer for håndtering av koronapandemien': {
    en: 'Guidelines for handling the coronavirus pandemic',
    sv: 'Riktlinjer för hantering av coronapandemin',
    da: 'Retningslinjer for håndtering af coronapandemien',
    pl: 'Wytyczne dotyczące postępowania w czasie pandemii koronawirusa',
  },
  'Selskapsavtale for DA og ANS': {
    en: 'Partnership agreement for DA and ANS (general partnerships)',
    sv: 'Bolagsavtal för DA och ANS',
    da: 'Selskabsaftale for DA og ANS',
    pl: 'Umowa spółki dla DA i ANS (spółki jawne)',
  },
  'Selskapsavtale for fysioterapeuter i DA med selskapstilskudd': {
    en: 'Partnership agreement for physiotherapists in a DA with company-allocated subsidy',
    sv: 'Bolagsavtal för fysioterapeuter i DA med bolagsbidrag',
    da: 'Selskabsaftale for fysioterapeuter i DA med selskabstilskud',
    pl: 'Umowa spółki dla fizjoterapeutów w DA z dotacją spółki',
  },
  'Selskapsavtale for fysioterapeuter med personlige tilskudd i DA': {
    en: 'Partnership agreement for physiotherapists with personal subsidies in a DA',
    sv: 'Bolagsavtal för fysioterapeuter med personliga bidrag i DA',
    da: 'Selskabsaftale for fysioterapeuter med personlige tilskud i DA',
    pl: 'Umowa spółki dla fizjoterapeutów z dotacjami osobistymi w DA',
  },
  Selskapsmøte: { en: 'Company meeting', sv: 'Bolagsstämma', da: 'Selskabsmøde', pl: 'Zgromadzenie wspólników' },
  'Sikkerhetkopi er tom når data skal tilbakekopieres': {
    en: 'Backup is empty when data is to be restored',
    sv: 'Säkerhetskopian är tom när data ska återställas',
    da: 'Sikkerhedskopien er tom, når data skal gendannes',
    pl: 'Kopia zapasowa jest pusta przy próbie przywrócenia danych',
  },
  'Sikkerhetkopi er tom når pasientdata skal tilbakekopieres': {
    en: 'Backup is empty when patient data is to be restored',
    sv: 'Säkerhetskopian är tom när patientdata ska återställas',
    da: 'Sikkerhedskopien er tom, når patientdata skal gendannes',
    pl: 'Kopia zapasowa jest pusta przy próbie przywrócenia danych pacjentów',
  },
  'Sjekkliste for opplæring': {
    en: 'Training checklist',
    sv: 'Checklista för utbildning',
    da: 'Tjekliste for oplæring',
    pl: 'Lista kontrolna szkolenia',
  },
  Sjømannslege: { en: "Seafarer's doctor", sv: 'Sjömansläkare', da: 'Søfartslæge', pl: 'Lekarz marynarzy' },
  'Skjerming av ansatte i risikogruppen ved koronapandemien': {
    en: 'Shielding of employees in the risk group during the coronavirus pandemic',
    sv: 'Skydd av anställda i riskgruppen under coronapandemin',
    da: 'Afskærmning af medarbejdere i risikogruppen under coronapandemien',
    pl: 'Ochrona pracowników z grupy ryzyka w czasie pandemii koronawirusa',
  },
  'Smittevern på tannlegekontoret': {
    en: 'Infection control at the dental office',
    sv: 'Smittskydd på tandläkarmottagningen',
    da: 'Infektionshygiejne på tandlægeklinikken',
    pl: 'Kontrola zakażeń w gabinecie stomatologicznym',
  },
  Smittevernansvarlig: {
    en: 'Infection control officer',
    sv: 'Smittskyddsansvarig',
    da: 'Infektionshygiejneansvarlig',
    pl: 'Osoba odpowiedzialna za kontrolę zakażeń',
  },
  'Standard for bruk av betegnelsen manuellterapeut': {
    en: 'Standard for use of the title manual therapist',
    sv: 'Standard för användning av beteckningen manuellterapeut',
    da: 'Standard for brug af betegnelsen manuel terapeut',
    pl: 'Standard używania tytułu terapeuty manualnego',
  },
  'Stiftelsesdokument for aksjeselskap': {
    en: 'Memorandum of incorporation for a limited liability company',
    sv: 'Stiftelseurkund för aktiebolag',
    da: 'Stiftelsesdokument for aktieselskab',
    pl: 'Akt założycielski spółki akcyjnej',
  },
  'Strålevern for ansatte': {
    en: 'Radiation protection for employees',
    sv: 'Strålskydd för anställda',
    da: 'Strålebeskyttelse for ansatte',
    pl: 'Ochrona radiologiczna pracowników',
  },
  Strålevernkoordinator: {
    en: 'Radiation protection coordinator',
    sv: 'Strålskyddssamordnare',
    da: 'Strålebeskyttelseskoordinator',
    pl: 'Koordynator ochrony radiologicznej',
  },
  Styremøte: { en: 'Board meeting', sv: 'Styrelsemöte', da: 'Bestyrelsesmøde', pl: 'Posiedzenie zarządu' },
  Sykeavbruddsforsikring: {
    en: 'Business interruption insurance (illness)',
    sv: 'Sjukavbrottsförsäkring',
    da: 'Sygeafbrydelsesforsikring',
    pl: 'Ubezpieczenie od przerwy w działalności z powodu choroby',
  },
  'Sykmelding under koronapandemien': {
    en: 'Sick leave during the coronavirus pandemic',
    sv: 'Sjukskrivning under coronapandemin',
    da: 'Sygemelding under coronapandemien',
    pl: 'Zwolnienie lekarskie podczas pandemii koronawirusa',
  },
  'Tannhelsesekretær/assistent': {
    en: 'Dental secretary/assistant',
    sv: 'Tandsköterska/assistent',
    da: 'Tandklinikassistent/assistent',
    pl: 'Sekretarka/asystentka stomatologiczna',
  },
  Tannlege: { en: 'Dentist', sv: 'Tandläkare', da: 'Tandlæge', pl: 'Dentysta' },
  Tannpleier: { en: 'Dental hygienist', sv: 'Tandhygienist', da: 'Tandplejer', pl: 'Higienistka stomatologiczna' },
  'Tiltak for uvaksinert helsepersonell': {
    en: 'Measures for unvaccinated healthcare personnel',
    sv: 'Åtgärder för ovaccinerad vårdpersonal',
    da: 'Tiltag for uvaccineret sundhedspersonale',
    pl: 'Środki dotyczące niezaszczepionego personelu medycznego',
  },
  'Tiltak mot datavirus og datainnbrudd': {
    en: 'Measures against computer viruses and data breaches',
    sv: 'Åtgärder mot datavirus och dataintrång',
    da: 'Tiltag mod computervirus og datasikkerhedsbrud',
    pl: 'Środki przeciwko wirusom komputerowym i włamaniom do systemów',
  },
  'Tiltak mot innsyn i personopplysninger': {
    en: 'Measures against unauthorised access to personal data',
    sv: 'Åtgärder mot insyn i personuppgifter',
    da: 'Tiltag mod indsigt i personoplysninger',
    pl: 'Środki przeciwko nieuprawnionemu dostępowi do danych osobowych',
  },
  Toppdokument: { en: 'Top-level document', sv: 'Toppdokument', da: 'Topdokument', pl: 'Dokument nadrzędny' },
  'Utlevering av helseopplysninger': {
    en: 'Disclosure of health information',
    sv: 'Utlämnande av hälsouppgifter',
    da: 'Udlevering af helbredsoplysninger',
    pl: 'Udostępnianie informacji o stanie zdrowia',
  },
  'Utredning og behandling av temporomandibulær dysfunksjon – TMD': {
    en: 'Assessment and treatment of temporomandibular dysfunction – TMD',
    sv: 'Utredning och behandling av temporomandibulär dysfunktion – TMD',
    da: 'Udredning og behandling af temporomandibulær dysfunktion – TMD',
    pl: 'Diagnostyka i leczenie dysfunkcji skroniowo-żuchwowej – TMD',
  },
  Utstyrsansvarlig: {
    en: 'Equipment manager',
    sv: 'Utrustningsansvarig',
    da: 'Udstyrsansvarlig',
    pl: 'Osoba odpowiedzialna za sprzęt',
  },
  'Uvedkommende får tak i passord og kan bruke EPJ': {
    en: 'Unauthorised person obtains password and can access the EHR',
    sv: 'Obehörig får tag i lösenord och kan använda EPJ',
    da: 'Uvedkommende får fat i adgangskode og kan bruge EPJ',
    pl: 'Osoba nieuprawniona zdobywa hasło i może korzystać z EHR',
  },
  'Uvedkommende får tilgang til personopplysninger på kassert utstyr': {
    en: 'Unauthorised person gains access to personal data on discarded equipment',
    sv: 'Obehörig får tillgång till personuppgifter på kasserad utrustning',
    da: 'Uvedkommende får adgang til personoplysninger på kasseret udstyr',
    pl: 'Osoba nieuprawniona uzyskuje dostęp do danych osobowych na wycofanym sprzęcie',
  },
  'Uvedkommende hører pasientopplysninger i fellesarealer': {
    en: 'Unauthorised person overhears patient information in common areas',
    sv: 'Obehörig hör patientuppgifter i gemensamma utrymmen',
    da: 'Uvedkommende hører patientoplysninger i fællesarealer',
    pl: 'Osoba nieuprawniona słyszy informacje o pacjentach w częściach wspólnych',
  },
  'Uvedkommende ser papir med personopplysninger': {
    en: 'Unauthorised person sees paper containing personal data',
    sv: 'Obehörig ser papper med personuppgifter',
    da: 'Uvedkommende ser papir med personoplysninger',
    pl: 'Osoba nieuprawniona widzi dokument papierowy z danymi osobowymi',
  },
  'Uvedkommende ser pasientopplysninger på dataskjerm': {
    en: 'Unauthorised person sees patient information on a computer screen',
    sv: 'Obehörig ser patientuppgifter på dataskärm',
    da: 'Uvedkommende ser patientoplysninger på computerskærm',
    pl: 'Osoba nieuprawniona widzi informacje o pacjencie na ekranie komputera',
  },
  'Uvedkommende ser personopplysninger på dataskjerm': {
    en: 'Unauthorised person sees personal data on a computer screen',
    sv: 'Obehörig ser personuppgifter på dataskärm',
    da: 'Uvedkommende ser personoplysninger på computerskærm',
    pl: 'Osoba nieuprawniona widzi dane osobowe na ekranie komputera',
  },
  Vaksinasjon: { en: 'Vaccination', sv: 'Vaccination', da: 'Vaccination', pl: 'Szczepienie' },
  'Varsel til førerkortinnehaver – helsekrav ikke oppfylt': {
    en: 'Notice to driving licence holder – health requirements not met',
    sv: 'Meddelande till körkortsinnehavare – hälsokrav inte uppfyllda',
    da: 'Meddelelse til kørekortindehaver – helbredskrav ikke opfyldt',
    pl: 'Powiadomienie dla posiadacza prawa jazdy – wymagania zdrowotne niespełnione',
  },
  'Varsling om kritikkverdige forhold': {
    en: 'Whistleblowing about censurable conditions',
    sv: 'Visselblåsning om kritikvärdiga förhållanden',
    da: 'Whistleblowing om kritisable forhold',
    pl: 'Sygnalizowanie nieprawidłowości',
  },
  'Vedtekter for aksjeselskap': {
    en: 'Articles of association for a limited company',
    sv: 'Bolagsordning för aktiebolag',
    da: 'Vedtægter for aktieselskab',
    pl: 'Statut spółki akcyjnej',
  },
  'Veileder for datasikkerhet': {
    en: 'Guide to data security',
    sv: 'Vägledning för datasäkerhet',
    da: 'Vejledning om datasikkerhed',
    pl: 'Przewodnik po bezpieczeństwie danych',
  },
  'Veiledere og andre ressurser': {
    en: 'Guides and other resources',
    sv: 'Vägledningar och andra resurser',
    da: 'Vejledninger og andre ressourcer',
    pl: 'Przewodniki i inne zasoby',
  },
  Verneombud: {
    en: 'Safety representative',
    sv: 'Skyddsombud',
    da: 'Arbejdsmiljørepræsentant',
    pl: 'Przedstawiciel ds. BHP',
  },
  'Vårt møte med pasientene': {
    en: 'Our encounter with patients',
    sv: 'Vårt möte med patienterna',
    da: 'Vores møde med patienterne',
    pl: 'Nasze podejście do pacjentów',
  },
  Yrkesskadeforsikring: {
    en: 'Occupational injury insurance',
    sv: 'Arbetsskadeförsäkring',
    da: 'Arbejdsskadeforsikring',
    pl: 'Ubezpieczenie od wypadków przy pracy',
  },
  Økonomiansvarlig: {
    en: 'Finance manager',
    sv: 'Ekonomiansvarig',
    da: 'Økonomiansvarlig',
    pl: 'Kierownik ds. finansów',
  },
};
